import { useCallback, useEffect, useState } from "react";
import { debounce } from "lodash";
import { DateRange } from "react-day-picker";
import { Table } from "@tanstack/react-table";
import { useTermValidationFilterActions } from "./actions";
import { useFilterState } from "./filterState";
import { formatFilterValue } from "@/utils/formatters";
import { ALL_OPTIONS } from "@/pages/store-back-office/generate-term/data/options";

export const useToolbarFilters = <TData,>(table: Table<TData>) => {
  const { updateFilter } = useTermValidationFilterActions();
  const filterState = useFilterState();

  const [searchValue, setSearchValue] = useState("");
  const [storeValue, setStoreValue] = useState<any[]>([]);
  const [statusValue, setStatusValue] = useState(ALL_OPTIONS);
  const [reasonValue, setReasonValue] = useState(ALL_OPTIONS);
  const [slaStatusValue, setSlaStatusValue] = useState(ALL_OPTIONS);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  useEffect(() => {
    const filters = (filterState as any)?.filters || {};
    if (filters.search) setSearchValue(filters.search);
    if (filters.store) setStoreValue(filters.store);
    if (filters.status) setStatusValue(filters.status);
    if (filters.reason) setReasonValue(filters.reason);
    if (filters.sla_status) setSlaStatusValue(filters.sla_status);
    if (filters.start_date && filters.end_date) {
      setDateRange({
        from: new Date(filters.start_date),
        to: new Date(filters.end_date),
      });
    }
  }, []);

  const debouncedUpdateFilter = useCallback(
    debounce((value: string) => {
      updateFilter({ key: "search", value });
      updateFilter({ key: "meta", value: { page: 1 } });
    }, 500),
    [updateFilter]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    debouncedUpdateFilter(value);
  };

  const handleStoreChange = (options: any) => {
    const selectedOptions = Array.isArray(options)
      ? options
      : options ? [options] : [];
    setStoreValue(selectedOptions);
    const values = selectedOptions.map((opt: any) => opt.value);
    updateFilter({ key: "store", value: formatFilterValue(values) || "" });
    updateFilter({ key: "meta", value: { page: 1 } });
  };

  const handleStatusChange = (value: string) => {
    setStatusValue(value);
    updateFilter({ key: "status", value: value === ALL_OPTIONS ? "" : value });
    updateFilter({ key: "meta", value: { page: 1 } });
  };

  const handleReasonChange = (value: string) => {
    setReasonValue(value);
    updateFilter({ key: "reason", value: value === ALL_OPTIONS ? "" : value });
    updateFilter({ key: "meta", value: { page: 1 } });
  };

  const handleSlaStatusChange = (value: string) => {
    setSlaStatusValue(value);
    updateFilter({ key: "sla_status", value: value === ALL_OPTIONS ? "" : value });
    updateFilter({ key: "meta", value: { page: 1 } });
  };

  const handleDateRangeChange = useCallback(
    (startDate: string | undefined, endDate: string | undefined) => {
      setDateRange({
        from: startDate ? new Date(startDate) : undefined,
        to: endDate ? new Date(endDate) : undefined,
      });
      updateFilter({ key: "start_date", value: startDate || "" });
      updateFilter({ key: "end_date", value: endDate || "" });
      updateFilter({ key: "meta", value: { page: 1 } });
    },
    [updateFilter]
  );

  const clearAllFilters = (dateRangePickerClear?: () => void) => {
  setSearchValue("");
  setStoreValue([]);
  setStatusValue(ALL_OPTIONS);
  setReasonValue(ALL_OPTIONS);
  setSlaStatusValue(ALL_OPTIONS);
  setDateRange(undefined);
  dateRangePickerClear?.(); 
    updateFilter({ key: "search", value: "" });
    updateFilter({ key: "store", value: "" });
    updateFilter({ key: "status", value: "" });
    updateFilter({ key: "reason", value: "" });
    updateFilter({ key: "sla_status", value: "" });
    updateFilter({ key: "start_date", value: "" });
    updateFilter({ key: "end_date", value: "" });
    updateFilter({ key: "orderBy", value: {} });
    updateFilter({ key: "meta", value: { page: 1 } });
    table.resetColumnFilters();
  };

  return {
    searchValue,
    storeValue,
    statusValue,
    reasonValue,
    slaStatusValue,
    dateRange,
    handleInputChange,
    handleStoreChange,
    handleStatusChange,
    handleReasonChange,
    handleSlaStatusChange,
    handleDateRangeChange,
    clearAllFilters,
  };
};